<script setup>
import { computed, ref } from 'vue';
import { changePassword, completeNewPassword } from '../utils/auth';

const props = defineProps({
  /** When set, this is the Cognito user object from a NEW_PASSWORD_REQUIRED challenge. */
  challengeUser: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['password-changed', 'cancel']);

const isForced = computed(() => !!props.challengeUser);

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const error = ref('');
const loading = ref(false);

const handleSubmit = async () => {
  error.value = '';
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'New passwords do not match.';
    return;
  }
  loading.value = true;
  try {
    if (isForced.value) {
      await completeNewPassword(props.challengeUser, newPassword.value);
    } else {
      await changePassword(currentPassword.value, newPassword.value);
    }
    emit('password-changed');
  } catch (err) {
    error.value = err.message || 'Password change failed. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="auth-page">
    <div class="auth-card">
      <h1>Wanderly</h1>
      <h2>{{ isForced ? 'Set Your Password' : 'Change Password' }}</h2>
      <p
        v-if="isForced"
        class="auth-hint"
      >
        You must set a new password before continuing.
      </p>
      <form
        class="auth-form"
        @submit.prevent="handleSubmit"
      >
        <div
          v-if="!isForced"
          class="form-group"
        >
          <label for="current-password">Current Password</label>
          <input
            id="current-password"
            v-model="currentPassword"
            type="password"
            autocomplete="current-password"
            required
            :disabled="loading"
          >
        </div>
        <div class="form-group">
          <label for="new-password">New Password</label>
          <input
            id="new-password"
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            required
            :disabled="loading"
          >
        </div>
        <div class="form-group">
          <label for="confirm-password">Confirm New Password</label>
          <input
            id="confirm-password"
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            required
            :disabled="loading"
          >
        </div>
        <p
          v-if="error"
          class="input-error"
        >
          {{ error }}
        </p>
        <div class="auth-actions">
          <button
            type="submit"
            class="btn-primary"
            :disabled="loading"
          >
            {{ loading ? 'Saving…' : (isForced ? 'Set Password' : 'Change Password') }}
          </button>
          <button
            v-if="!isForced"
            type="button"
            class="btn-secondary"
            :disabled="loading"
            @click="emit('cancel')"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
